/**
 * GitHub frame branch picker — Radix DropdownMenu with search and grouped
 * branch options (Current, Active coder branches, All branches).
 *
 * When `locked=true` the branch is shown with a lock icon and no dropdown
 * trigger — this reflects the fact that the frame is bound to an active
 * task whose branch should not be changed from the UI.
 */

import { useMemo, useRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PocketSection, TextInput } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesGithubMessages } from "@/i18n/messages/frames-github";
import { cn } from "@/lib/cn";
import { useBranches } from "@/hooks/use-branches";
import type { ActiveCoderBranch } from "@/api/branches";

interface BranchPickerProps {
  projectId: string | null | undefined;
  currentBranch: string;
  onSelect: (branch: string) => void;
  locked?: boolean;
  /** The menu opened or closed (an opened window's pill stays shown). */
  onOpenChange?: (open: boolean) => void;
}

export function BranchPicker({
  projectId,
  currentBranch,
  onSelect,
  locked = false,
  onOpenChange,
}: BranchPickerProps) {
  const t = useT(framesGithubMessages);
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);
  const { data, isLoading, error } = useBranches(
    open && !locked ? projectId : null,
  );

  const q = query.trim().toLowerCase();
  const matchesQuery = (s: string) => !q || s.toLowerCase().includes(q);

  const activeCoderBranches: ActiveCoderBranch[] = useMemo(
    () =>
      (data?.active_coder_branches ?? []).filter((b) =>
        matchesQuery(b.branch_name),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, q],
  );

  const defaultBranch = data?.default ?? "";
  const showDefault =
    !!defaultBranch &&
    defaultBranch !== currentBranch &&
    matchesQuery(defaultBranch);

  const activeCoderSet = useMemo(
    () =>
      new Set((data?.active_coder_branches ?? []).map((b) => b.branch_name)),
    [data],
  );

  const allBranches = useMemo(() => {
    const list = data?.all_branches ?? [];
    return list.filter(
      (b) =>
        b !== currentBranch &&
        b !== defaultBranch &&
        !activeCoderSet.has(b) &&
        matchesQuery(b),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, currentBranch, defaultBranch, activeCoderSet, q]);

  const handleSelect = (branch: string) => {
    setOpen(false);
    setQuery("");
    if (branch !== currentBranch) onSelect(branch);
  };

  if (locked) {
    return (
      <span
        className="inline-flex min-w-0 items-center gap-1 truncate text-canvas-sm font-semibold text-secondary"
        data-testid="github-frame-branch-picker-locked"
      >
        <Icons.Lock
          className="h-2.5 w-2.5 shrink-0 text-muted"
          data-testid="github-frame-lock-icon"
          aria-hidden="true"
        />
        <span className="truncate">{currentBranch || "—"}</span>
      </span>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex min-w-0 max-w-full items-center gap-1 truncate rounded-control-sm px-1 py-0.5",
            "text-canvas-sm font-semibold text-secondary hover:text-primary hover:bg-btn-ghost-fill",
            "outline-none focus-visible:ring-1",
          )}
          onClick={(e) => e.stopPropagation()}
          data-testid="github-frame-branch-picker-trigger"
        >
          <Icons.GitBranch
            className="h-2.5 w-2.5 shrink-0 text-muted"
            aria-hidden="true"
          />
          <span className="truncate">{currentBranch || t("selectBranch")}</span>
          <Icons.ChevronDown
            className="h-2.5 w-2.5 shrink-0 text-muted"
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="flex w-80 max-h-[min(480px,calc(100vh-16px))] flex-col p-3"
        onFocusCapture={(event) => {
          if (event.target === event.currentTarget)
            queueMicrotask(() =>
              searchRef.current?.focus({ preventScroll: true }),
            );
        }}
        data-testid="github-frame-branch-picker-menu"
      >
        <div className="relative shrink-0">
          <Icons.Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />
          <TextInput
            ref={searchRef}
            type="search"
            shape="capsule"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("filterBranchesPlaceholder")}
            aria-label={t("filterBranches")}
            className="pl-9"
            data-testid="github-frame-branch-picker-search"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget
                  .closest('[data-testid="github-frame-branch-picker-menu"]')
                  ?.querySelector<HTMLElement>('[role="menuitem"]')
                  ?.focus();
                return;
              }
              if (e.key !== "Escape") e.stopPropagation();
            }}
          />
        </div>

        <div className="scrollbar-none min-h-0 overflow-y-auto">
          <PocketSection title={t("current")}>
            {matchesQuery(currentBranch) && currentBranch ? (
              <BranchRow
                branch={currentBranch}
                onSelect={handleSelect}
                selected
              />
            ) : (
              <EmptyHint>{t("noMatch")}</EmptyHint>
            )}
          </PocketSection>

          {(activeCoderBranches.length > 0 || showDefault) && (
            <PocketSection title={t("activeCoderBranches")}>
              {showDefault && (
                <BranchRow
                  branch={defaultBranch}
                  onSelect={handleSelect}
                  subtitle={t("defaultBranch")}
                />
              )}
              {activeCoderBranches.map((b) => (
                <BranchRow
                  key={b.branch_name}
                  branch={b.branch_name}
                  onSelect={handleSelect}
                  subtitle={
                    b.task_ticket
                      ? `${b.task_ticket} · ${b.task_title}`
                      : b.task_title
                  }
                  agentName={b.agent_name ?? undefined}
                  agentColor={b.agent_color ?? undefined}
                />
              ))}
            </PocketSection>
          )}

          {allBranches.length > 0 && (
            <PocketSection title={t("allBranches")}>
              {allBranches.map((b) => (
                <BranchRow key={b} branch={b} onSelect={handleSelect} />
              ))}
            </PocketSection>
          )}

          {isLoading && !data && (
            <EmptyHint>
              <Icons.Spinner className="h-3 w-3 animate-spin" />
              {t("loadingBranches")}
            </EmptyHint>
          )}
          {!isLoading && error && (
            <EmptyHint>
              {error.status === 404
                ? t("noRepoLinked")
                : t("loadBranchesFailed", {
                    reason: error.status ?? t("network"),
                  })}
            </EmptyHint>
          )}
          {!isLoading &&
            !error &&
            data &&
            activeCoderBranches.length === 0 &&
            allBranches.length === 0 &&
            !showDefault && <EmptyHint>{t("noOtherBranches")}</EmptyHint>}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-2 text-appkit-small font-medium text-muted">
      {children}
    </div>
  );
}

interface BranchRowProps {
  branch: string;
  onSelect: (branch: string) => void;
  selected?: boolean;
  subtitle?: string;
  agentName?: string;
  agentColor?: string;
}

function BranchRow({
  branch,
  onSelect,
  selected = false,
  subtitle,
  agentName,
  agentColor,
}: BranchRowProps) {
  return (
    <DropdownMenuItem
      onSelect={() => onSelect(branch)}
      className={cn(
        "min-h-11 w-full gap-2 rounded-[16px] px-2 py-1.5 text-left",
        subtitle && "min-h-[46px]",
        selected && "bg-[var(--color-interactive-hover)]",
      )}
      data-testid={`github-frame-branch-picker-opt-${branch}`}
    >
      <Icons.GitBranch
        className="h-4 w-4 shrink-0 text-muted"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-appkit-body font-medium">
          {branch}
        </span>
        {subtitle && (
          <span className="block truncate text-appkit-small font-medium text-muted">
            {subtitle}
          </span>
        )}
      </span>
      {agentName && (
        <span className="flex shrink-0 items-center gap-1">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: agentColor ?? "var(--color-muted)" }}
            aria-hidden="true"
          />
          <span className="max-w-[80px] truncate text-appkit-small font-medium text-muted">
            {agentName}
          </span>
        </span>
      )}
      {selected && (
        <Icons.Check
          className="h-4 w-4 shrink-0 text-accent"
          aria-hidden="true"
        />
      )}
    </DropdownMenuItem>
  );
}
